import { useEffect, useState } from 'react';
import { MessageSquare, Star, X } from 'lucide-react';
import { lmsAPI } from '../services/api';
import { formatDate } from '../utils/dateHelpers';
import { InlineLoader } from './loading';

/**
 * What attendees said about a session.
 *
 * The learner-side feedback prompt, the lms_session_feedback table and
 * GET /lms/admin/sessions/:id/feedback all shipped together — but nothing in
 * the portal ever called that endpoint, so every rating and comment a learner
 * submitted went into the database and was never read by anyone. This is the
 * screen the endpoint was written for.
 */

interface FeedbackComment {
  rating: number;
  feedback_text: string;
  /** Who the attendee says actually delivered it — often not who was booked. */
  trainer_name: string | null;
  created_at: string;
}

interface FeedbackQuestion {
  key: string;
  label: string;
  response_count: number;
  average_rating: number | null;
}

interface FeedbackSection {
  key: string;
  title: string;
  questions: FeedbackQuestion[];
}

interface FeedbackSummary {
  response_count: number;
  average_rating: number | null;
  positive_count: number;
  rating_max?: number;
  /** Per-question averages, in catalogue order. Absent on an older API. */
  sections?: FeedbackSection[];
  comments: FeedbackComment[];
}

interface LmsSessionFeedbackModalProps {
  sessionId: string;
  sessionTitle: string;
  projectId?: number;
  onClose: () => void;
}

/** Five stars with the filled count matching `rating`. */
function StarRating({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-3.5 h-3.5 ${
            star <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'
          }`}
        />
      ))}
    </span>
  );
}

export default function LmsSessionFeedbackModal({
  sessionId,
  sessionTitle,
  projectId,
  onClose,
}: LmsSessionFeedbackModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<FeedbackSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await lmsAPI.getSessionFeedback(sessionId, projectId);
        if (cancelled) return;
        setFeedback(response.data?.data || null);
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading session feedback:', err);
        setError('Could not load feedback for this session.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, projectId]);

  const responseCount = feedback?.response_count ?? 0;
  const positiveShare =
    responseCount > 0 ? Math.round(((feedback?.positive_count ?? 0) / responseCount) * 100) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-100 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Attendee Feedback</h2>
            <p className="mt-0.5 text-sm text-gray-500">{sessionTitle}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
              <InlineLoader label="Loading feedback…" />
            </div>
          ) : error ? (
            <p className="py-10 text-center text-sm text-red-600">{error}</p>
          ) : responseCount === 0 ? (
            <div className="py-10 text-center">
              <MessageSquare className="mx-auto w-8 h-8 text-gray-300" />
              <p className="mt-2 text-sm text-gray-500">
                No feedback yet. Attendees are asked to rate the session after they check in.
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                  <p className="text-xs text-gray-500">Responses</p>
                  <p className="mt-1 text-xl font-semibold text-gray-900">{responseCount}</p>
                </div>
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                  <p className="text-xs text-gray-500">Average rating</p>
                  <p className="mt-1 flex items-center gap-1.5 text-xl font-semibold text-gray-900">
                    {feedback?.average_rating?.toFixed(2) ?? '—'}
                    <span className="text-xs font-normal text-gray-500">/ 5</span>
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                  <p className="text-xs text-gray-500">Rated 4 or 5</p>
                  <p className="mt-1 text-xl font-semibold text-gray-900">
                    {feedback?.positive_count ?? 0}
                    {positiveShare !== null && (
                      <span className="ml-1 text-xs font-normal text-gray-500">
                        ({positiveShare}%)
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {/* Per-question averages. The overall star says how good the
                  session was; this says which part of it. Questions nobody
                  answered are still listed — an unanswered question is itself
                  a finding, and a list whose shape moves with the data cannot
                  be compared between sessions. */}
              {(feedback?.sections?.length ?? 0) > 0 && (
                <div className="space-y-4">
                  {feedback?.sections?.map((section) => (
                    <div key={section.key}>
                      <h3 className="mb-2 text-sm font-medium text-gray-700">{section.title}</h3>
                      <ul className="space-y-1.5">
                        {section.questions.map((question) => {
                          const max = feedback?.rating_max ?? 5;
                          const average = question.average_rating;
                          const share = average === null ? 0 : (average / max) * 100;
                          return (
                            <li key={question.key} className="flex items-center gap-3">
                              <span className="min-w-0 flex-1 truncate text-sm text-gray-700" title={question.label}>
                                {question.label}
                              </span>
                              <span className="h-1.5 w-24 shrink-0 overflow-hidden rounded-full bg-gray-100">
                                <span
                                  className={`block h-full rounded-full ${
                                    average !== null && average < 3 ? 'bg-red-400' : 'bg-primary-500'
                                  }`}
                                  style={{ width: `${share}%` }}
                                />
                              </span>
                              <span className="w-20 shrink-0 text-right text-xs text-gray-500">
                                {average === null ? (
                                  'No answers'
                                ) : (
                                  <>
                                    <span className="font-medium text-gray-900">
                                      {average.toFixed(2)}
                                    </span>
                                    {` / ${max} (${question.response_count})`}
                                  </>
                                )}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              )}

              <div>
                <h3 className="mb-2 text-sm font-medium text-gray-700">
                  Comments ({feedback?.comments.length ?? 0})
                </h3>
                {(feedback?.comments.length ?? 0) === 0 ? (
                  <p className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-4 text-center text-sm text-gray-500">
                    Ratings were submitted without any written comments.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {feedback?.comments.map((comment, index) => (
                      <li
                        key={`${comment.created_at}-${index}`}
                        className="rounded-lg border border-gray-100 p-3"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <StarRating rating={comment.rating} />
                          <span className="text-xs text-gray-400">
                            {comment.trainer_name ? `${comment.trainer_name} • ` : ''}
                            {formatDate(comment.created_at)}
                          </span>
                        </div>
                        {/* Feedback is anonymous to the admin by design: the
                            endpoint returns rating, text and time, never the
                            employee who wrote it. */}
                        <p className="mt-1.5 whitespace-pre-wrap text-sm text-gray-700">
                          {comment.feedback_text}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end border-t border-gray-100 px-6 py-3">
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
